import React, { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { IBasicSession } from 'types/basicSession';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDuration } from 'utils/formatDuration';
import CircularProgress from '@mui/material/CircularProgress';
import { useMarkSessionCompleteMutation } from 'hooks/sessions';
import DataTable, { TableColumn } from 'react-data-table-component';

interface ISessionListProps {
  sessionList: IBasicSession[];
  error: any;
  loading: boolean;
  refetch: () => any;
}

const SessionList = ({ sessionList, loading, error, refetch }: ISessionListProps) => {
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');

  const { mutateAsync: markSessionComplete, isLoading: isMarkingSessionComplete } =
    useMarkSessionCompleteMutation();

  const handleRowClicked = (row: IBasicSession) => {
    history.push(`/dashboard/edit-session/${row._id}`);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value.toLowerCase();
    setSearchQuery(keyword);
  };

  const markCompleteClicked = async (row: IBasicSession) => {
    try {
      if (!row._id) return;
      const response = await markSessionComplete(row._id);

      if (response.data) {
        toast.success('Session marked as complete');
      }
    } catch (error) {
      toast.error('Error marking session as complete');
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: (row: IBasicSession) => row.title,
      sortable: true,
      grow: 1,
    },
    {
      name: 'Status',
      selector: (row: IBasicSession) => row.status,
      sortable: true,
    },
    {
      name: 'Number of kids',
      selector: (row: IBasicSession) => row.numberOfKids,
      sortable: true,
    },
    {
      name: 'Sessions per week',
      selector: (row: IBasicSession) => row.sessionsPerWeek,
      sortable: true,
    },
    {
      name: 'Duration',
      selector: (row: IBasicSession) => formatDuration(row.duration || ''),
      sortable: true,
    },
    {
      name: 'Payment Status',
      selector: (row: IBasicSession) => row.paymentStatus,
    },
    {
      name: 'Action',
      grow: 2,
      cell: (row: IBasicSession) => (
        <div className="btn-group text-nowrap" role="group" aria-label="Basic example">
          <button
            type="button"
            style={{ fontSize: '14px' }}
            className="btn btn-warning me-2 "
            onClick={() => handleRowClicked(row)}
            disabled={row.status === 'completed'}
          >
            Edit
          </button>
          <button
            type="button"
            style={{ fontSize: '14px' }}
            className="btn btn-success me-2"
            onClick={() => markCompleteClicked(row)}
            disabled={row.status === 'completed'}
          >
            Mark complete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ].filter(Boolean) as TableColumn<any>[];

  const customStyles = {
    headRow: {
      style: {
        minWidth: '100%',
      },
    },
    headCells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    search: {
      style: {
        position: 'relative',
        width: '100%',
      },
    },
    searchInput: {
      style: {
        width: '100%',
      },
    },
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div className="d-flex align-items-center w-100">
        <input
          type="text"
          placeholder="Search sessions..."
          onChange={handleSearch}
          className="form-control me-2"
          value={searchQuery}
        />
        <div className="cursor-pointer" onClick={() => setSearchQuery('')}>
          <RefreshIcon />
        </div>
      </div>
    );
  }, [searchQuery]);

  return (
    <div className="w-100">
      {error && (
        <div>
          <p>Error fetching session data</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 btn btn-primary btn-block w-100 d-flex align-items-center justify-content-center"
          >
            Refetch
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </div>
      ) : (
        <DataTable
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          data={sessionList}
          columns={columns}
          pagination
          disabled={isMarkingSessionComplete}
          paginationPerPage={10}
          customStyles={customStyles}
          onRowClicked={handleRowClicked}
          highlightOnHover
          pointerOnHover
          paginationComponentOptions={{
            noRowsPerPage: true,
          }}
        />
      )}
    </div>
  );
};

export default SessionList;
